/**
 * Geometry for placing the scale-check ("size-matching") squares on a pattern
 * sheet when there's no cover page to host them (#577). Kept dependency-free so
 * it's unit-testable without pulling in jsPDF / svg2pdf.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A printed-geometry outline as a flattened polygon (page mm). Treated as a
 *  filled region for overlap tests. */
export type Poly = Array<[number, number]>;

/** Where the reserved scale-check band goes relative to the pattern. */
export type ScaleBandPosition = "top" | "bottom";

/** One tiling row in the reserved-band layout (#577). `tileY` is the pattern-y
 *  of the row's top; `slotH` the pattern height it shows (the band row is
 *  shorter by the band height); `band` says whether this row carries the band
 *  (and on which side). */
export interface ScaleBandRow {
  tileY: number;
  slotH: number;
  band: "none" | "top" | "bottom";
}

/** Lay the pattern out in rows when a band of height `bandH` must be reserved
 *  for the scale-check squares — either above the pattern (`top`, the first row
 *  is short) or below it (`bottom`, the last row is short). Total rows are the
 *  same either way, so the page count doesn't depend on the side. */
export function planScaleBandRows(
  boundsY: number,
  boundsH: number,
  fullH: number,
  bandH: number,
  position: ScaleBandPosition,
): ScaleBandRow[] {
  const short = fullH - bandH;
  // One row is short (loses `bandH`), so capacity is rowCount*fullH - bandH.
  const rowCount = Math.max(1, Math.ceil((boundsH + bandH) / fullH));
  const rows: ScaleBandRow[] = [];
  let y = boundsY;
  for (let r = 0; r < rowCount; r += 1) {
    const isBandRow = position === "top" ? r === 0 : r === rowCount - 1;
    const slotH = isBandRow ? short : fullH;
    rows.push({ tileY: y, slotH, band: isBandRow ? position : "none" });
    y += slotH;
  }
  return rows;
}

/** Internal row boundaries (pattern-y) — the page-break cut lines. */
function rowCutLines(rows: ScaleBandRow[]): number[] {
  return rows.slice(1).map((r) => r.tileY);
}

/** How many obstacle polygons straddle any of the cut lines (i.e. get split
 *  across pages). */
function countSplitPieces(cutYs: number[], obstacles: Poly[]): number {
  let count = 0;
  for (const poly of obstacles) {
    if (poly.length === 0) continue;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [, py] of poly) {
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    }
    if (cutYs.some((c) => c > minY && c < maxY)) count += 1;
  }
  return count;
}

/** Choose where the reserved band goes (#577): above the pattern by default,
 *  but below it when that splits fewer pieces across page boundaries — provided
 *  it doesn't add pages (it never does; both sides yield the same row count). */
export function chooseScaleBandRows(
  boundsY: number,
  boundsH: number,
  fullH: number,
  bandH: number,
  obstacles: Poly[],
): { position: ScaleBandPosition; rows: ScaleBandRow[] } {
  const above = planScaleBandRows(boundsY, boundsH, fullH, bandH, "top");
  const below = planScaleBandRows(boundsY, boundsH, fullH, bandH, "bottom");
  if (below.length === above.length) {
    const splitsAbove = countSplitPieces(rowCutLines(above), obstacles);
    const splitsBelow = countSplitPieces(rowCutLines(below), obstacles);
    if (splitsBelow < splitsAbove) return { position: "bottom", rows: below };
  }
  return { position: "top", rows: above };
}

/** Minimum clear distance (mm) between the on-pattern scale-check squares and
 *  any printed geometry — 1/2 inch (#577). */
export const SCALE_CHECK_CLEARANCE_MM = 0.5 * 25.4; // 12.7 mm

/**
 * Where and how to anchor the scale-check caption above the squares. The caption
 * can be a touch wider than the squares block, so it's right-aligned to the
 * block's right edge when the block sits on the right side of the page — or
 * whenever a left-aligned caption would spill past the printable border — so the
 * text never overflows the cut line. Otherwise it's left-aligned to the block.
 */
export function scaleCheckCaptionAnchor(
  spotX: number,
  blockW: number,
  content: Rect,
  captionW: number,
  edge = 3,
): { x: number; align: "left" | "right" } {
  const contentRight = content.x + content.w;
  const rightAligned =
    spotX + blockW / 2 > content.x + content.w / 2 ||
    spotX + captionW > contentRight - edge;
  return rightAligned
    ? { x: spotX + blockW, align: "right" }
    : { x: spotX, align: "left" };
}

/** Is point (px,py) inside the polygon? Even-odd ray casting. */
function pointInPoly(px: number, py: number, poly: Poly): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!;
    const [xj, yj] = poly[j]!;
    const straddles = yi > py !== yj > py;
    if (straddles && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** Do the two segments p1→p2 and p3→p4 cross? */
function segCross(
  p1: [number, number],
  p2: [number, number],
  p3: [number, number],
  p4: [number, number],
): boolean {
  const d = (a: number[], b: number[], c: number[]) =>
    (b[0]! - a[0]!) * (c[1]! - a[1]!) - (b[1]! - a[1]!) * (c[0]! - a[0]!);
  const d1 = d(p3, p4, p1);
  const d2 = d(p3, p4, p2);
  const d3 = d(p1, p2, p3);
  const d4 = d(p1, p2, p4);
  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  );
}

/** Does the axis-aligned rectangle overlap the filled polygon? True if any rect
 *  corner is inside the polygon, any polygon vertex is inside the rect, or any
 *  edges cross. */
export function rectHitsPoly(rect: Rect, poly: Poly): boolean {
  if (poly.length < 2) return false;
  const x0 = rect.x;
  const y0 = rect.y;
  const x1 = rect.x + rect.w;
  const y1 = rect.y + rect.h;
  // A rect corner inside the polygon → overlap.
  if (
    pointInPoly(x0, y0, poly) ||
    pointInPoly(x1, y0, poly) ||
    pointInPoly(x1, y1, poly) ||
    pointInPoly(x0, y1, poly)
  ) {
    return true;
  }
  const corners: Array<[number, number]> = [
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ];
  for (let i = 0; i < poly.length; i += 1) {
    const a = poly[i]!;
    // A polygon vertex inside the rect → overlap.
    if (a[0] >= x0 && a[0] <= x1 && a[1] >= y0 && a[1] <= y1) return true;
    const b = poly[(i + 1) % poly.length]!;
    // A polygon edge crossing any rect edge → overlap.
    for (let c = 0; c < 4; c += 1) {
      if (segCross(a, b, corners[c]!, corners[(c + 1) % 4]!)) return true;
    }
  }
  return false;
}

/**
 * Find a top-left point inside `content` where a `blockW × blockH` rectangle,
 * grown by {@link SCALE_CHECK_CLEARANCE_MM} on every side, intersects none of
 * `obstacles` (each a filled outline polygon). Prefers the four corners in
 * order — top-left, top-right, bottom-left, bottom-right — and only then the
 * first free slot scanning top-to-bottom (left-to-right) on a coarse step. All
 * coordinates share one space (page mm). Returns null when nothing clears —
 * either the block can't fit the printable area, or every position is too close
 * to geometry.
 */
export function findScaleCheckSpot(
  content: Rect,
  blockW: number,
  blockH: number,
  obstacles: Poly[],
  options: { clearance?: number; edge?: number; step?: number } = {},
): { x: number; y: number } | null {
  const clr = options.clearance ?? SCALE_CHECK_CLEARANCE_MM;
  const edge = options.edge ?? 3; // keep the block just inside the printable area
  const step = options.step ?? 5;

  // Range for the block's top-left corner so the block stays within `content`.
  const minX = content.x + edge;
  const minY = content.y + edge;
  const maxX = content.x + content.w - edge - blockW;
  const maxY = content.y + content.h - edge - blockH;
  if (maxX < minX || maxY < minY) return null; // block can't fit at all

  const fits = (x: number, y: number): boolean => {
    const ring: Rect = {
      x: x - clr,
      y: y - clr,
      w: blockW + clr * 2,
      h: blockH + clr * 2,
    };
    return !obstacles.some((o) => rectHitsPoly(ring, o));
  };

  // All four corners first (top-left, top-right, bottom-left, bottom-right),
  // then a row-major scan of the interior.
  const corners: Array<[number, number]> = [
    [minX, minY],
    [maxX, minY],
    [minX, maxY],
    [maxX, maxY],
  ];
  for (const [x, y] of corners) {
    if (fits(x, y)) return { x, y };
  }
  for (let y = minY; y <= maxY; y += step) {
    for (let x = minX; x <= maxX; x += step) {
      if (fits(x, y)) return { x, y };
    }
  }
  return null;
}
